import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import type { components } from '@/api/schema'
import { formatBsDate } from '@/lib/bsDate'
import {
  railMoversSchema,
  railUpdatesSchema,
  yearRangeSchema,
  type RailMovers,
  type RailUpdate,
  type YearRange,
} from '@/features/market-rail/api/railSchemas'

/**
 * The rail's own payloads: the updates list, top movers and the year range.
 *
 * ── Why this is not `overviewService.overview()` ──────────────────────────
 *
 * Both figures are fields of the Market Overview payload today, and reusing
 * that hook would have been one import. Its `queryFn` fans out to TEN raw
 * endpoints in a single `Promise.all` — indices, indexHistory, summary,
 * status, movers x2, sectors, brokers, events, updates
 * (`features/market-overview/api/overviewService.ts:44-56`). The rail needs
 * two of them. Every screen that mounts the rail — Stock, Account, Screener,
 * Portfolio, Watchlist, none of which show gainers, losers, sector tiles,
 * broker highlights or the events calendar — would have paid for that whole
 * fan-out to read 2 of its 12 fields, on top of the 4-endpoint fan-out
 * `market-now` already costs.
 *
 * ── What it is instead ────────────────────────────────────────────────────
 *
 * `marketEndpoints(client)` — the per-raw-endpoint cache in `src/api/endpointCache.ts`,
 * used exactly as `hooks/useMarketStatus.ts:82` uses it. Two calls, two cache
 * entries, and both of those entries are ones the app already populates:
 *
 *   summary()                        also fetched by marketService.indexSummary
 *                                    (so `market-now`, in this same rail, and
 *                                    Home's hero card) and by the overview
 *                                    fan-out on /market
 *   updates({page:1,page_size:5})    byte-identical arguments to
 *                                    overviewService.ts:55, so /market shares
 *                                    this entry rather than re-requesting it
 *
 * The argument list IS the cache key (see `endpointKey` in endpointCache.ts),
 * which is why `UPDATE_PAGE_SIZE` is 5 and not 4 even though the rail draws
 * four rows: asking for `page_size: 4` would hash to a different key, miss the
 * entry Market Overview already filled, and put a second request on the wire
 * for a subset of bytes we already had. Fetch what the app fetches; slice in
 * the component.
 */

/** Matches `overviewService.ts:55` exactly. See the note above on why. */
export const UPDATE_PAGE_SIZE = 5

/**
 * The payload's `direction` is a free string; the tone vocabulary is closed.
 * Re-derived here rather than imported because `overviewService`'s copy is a
 * module-private helper — three lines is a cheaper dependency than widening
 * another feature's surface for one caller.
 */
const toneFromDirection = (direction: string | null) => {
  if (direction === 'up') return 'up' as const
  if (direction === 'down') return 'down' as const
  return 'flat' as const
}

/** One row of `/market/updates/`, as the endpoint cache returns it. */
type UpdateRow = Awaited<ReturnType<ReturnType<typeof marketEndpoints>['updates']>>['rows'][number]

/**
 * One `/market/updates/` row as a rail row, before `railUpdatesSchema` parses
 * the list. Exported so every list of updates — the rail's, Stock's "News
 * about {symbol}" — maps the wire the same way: an IPO note links nowhere, a
 * company note links to its stock, a market-wide note has neither.
 */
export const toRailUpdate = (row: UpdateRow) => ({
  id: String(row.id),
  title: row.title,
  body: row.body,
  symbol: row.entity ?? 'MARKET',
  tone: toneFromDirection(row.direction),
  ...(row.kind === 'ipo' ? { isIpo: true } : {}),
  ...(row.entity !== null && row.entity_type === 'company' && row.kind !== 'ipo'
    ? { stockSymbol: row.entity }
    : {}),
  ...(row.entity !== null && row.entity_type === 'sector' ? { sector: row.entity } : {}),
  publishedOn: formatBsDate(row.published_at),
})

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** The Top movers block's rows: the mock's two a side. */
const MOVERS_A_SIDE = 2

/**
 * The movers feed's call, with the very arguments Market · Overview's fan-out uses
 * (`overviewService`), so on `/market` the rail reads the entries the page already holds.
 */
const MOVERS_LIMIT = 5

const toRailMover = (row: Wire<'MoverRow'>) => ({
  symbol: row.key,
  ltp: row.ltp,
  percentChange: row.change_percent,
})

/** Two decimals, as mm-be stores the index levels. */
const sameLevel = (a: string | null, b: string | null): boolean =>
  a !== null && b !== null && Math.round(Number(a) * 100) === Math.round(Number(b) * 100)

/**
 * The session a 52-week bound was set in: the first point of the year's history whose low (for
 * the low) or high (for the high), or close, is that level to the paisa. Null when no point is —
 * the history is daily and can miss the day, and a near miss is not the day.
 */
const dayOf = (
  points: readonly Wire<'IndexPoint'>[],
  level: string,
  side: 'low' | 'high',
): string | null =>
  points.find((point) => sameLevel(point[side], level) || sameLevel(point.close, level))?.t ?? null

export const railService = {
  updates: async (client?: QueryClient): Promise<RailUpdate[]> => {
    const page = await marketEndpoints(client).updates({
      page: 1,
      page_size: UPDATE_PAGE_SIZE,
    })

    return railUpdatesSchema.parse(page.rows.map(toRailUpdate))
  },

  /** Top movers: the day's two biggest gainers, then its two biggest fallers. */
  topMovers: async (client?: QueryClient): Promise<RailMovers> => {
    const api = marketEndpoints(client)
    const [gainers, losers] = await Promise.all([
      api.movers({ type: 'gainers', limit: MOVERS_LIMIT }),
      api.movers({ type: 'losers', limit: MOVERS_LIMIT }),
    ])

    return railMoversSchema.parse({
      gainers: gainers.rows.slice(0, MOVERS_A_SIDE).map(toRailMover),
      losers: losers.rows.slice(0, MOVERS_A_SIDE).map(toRailMover),
    })
  },

  /**
   * The 52-week range: `week52` from the summary, and the day each bound was set, from the year's
   * history. Null when the summary has no year range to place today in.
   */
  yearRange: async (client?: QueryClient): Promise<YearRange | null> => {
    const api = marketEndpoints(client)
    const [summary, history] = await Promise.all([api.summary(), api.indexHistory('NEPSE', '1Y')])
    const { week52 } = summary
    if (week52 === null || week52.low === null || week52.high === null) return null
    if (Number(week52.high) <= Number(week52.low)) return null

    return yearRangeSchema.parse({
      now: week52.current,
      low: week52.low,
      high: week52.high,
      lowOn: dayOf(history.points, week52.low, 'low'),
      highOn: dayOf(history.points, week52.high, 'high'),
    })
  },
}
